import clsx from "clsx";
import React, { FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { useClickOutside } from "../hooks/useClickOutSide";
import { toAgeString, toNumberString } from "../utils/common";
import { ProdAttrUtils } from "../utils/productAttributeUtils";
import Icon from "./image/Icon";
import { icArrowDown } from "./image/image-src";
import { NumPicker } from "./QuantiySelector";

interface SelectChildAgeProps {
  onChange?: (value: AgeQuantityDTO[]) => void;
  ageQuantities?: AgeQuantityDTO[];
  ages: number[];
  disabled?: boolean;
  singleMode?: boolean; // If true, only one child can be selected
}

export const SelectChildAge: React.FC<
  SelectChildAgeProps & {
    className?: string;
  }
> = ({ className, ...props }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const onClose = () => {
    setOpen(false);
  };
  // const [childQuantity, setChildQuantity] = useState(0);
  // const adultOnlyLs = (props.value ?? []).filter((x) => x.age === "ADULT");
  const childOnlyLs = (props.ageQuantities ?? []).filter(
    (x) => x.age !== "ADULT",
  );
  const total = childOnlyLs
    .map((x) => x.quantity)
    .reduce((acc, x) => acc + x, 0);
  const ageStr = toAgeString(childOnlyLs?.[0]?.age);
  return (
    <>
      <div className="relative w-full">
        <button
          disabled={props.disabled}
          type="button"
          className={twMerge(
            "flex h-full w-full items-center justify-between rounded-[1rem] border border-[#DCDCDC] bg-white px-4 py-[0.875rem] sm:px-5 sm:py-[0.75rem]",
            className,
          )}
          onClick={() => setOpen(!open)}
        >
          <span className="text-base font-semibold leading-[1.625rem]">
            {props.singleMode && !!ageStr
              ? ageStr === "<1"
                ? t("Child <1 age")
                : t("Child {{age}} age", {
                    age: ageStr,
                  })
              : t("Select child") + " " + (total ? `(${total})` : "")}
          </span>
          <Icon src={icArrowDown} />
        </button>
        {open && (
          <SelectChildAgeDropdownComponent {...props} onClose={onClose} />
        )}
      </div>
    </>
  );
};

{
  /* <QuantityCard
        title={t("Child")}
        value={childQuantity}
        onChange={(quantity) => {
          setChildQuantity(quantity);
          if (quantity === 0) {
            props.onChange?.([...adultOnlyLs]);
          }
        }}
      />
      {childQuantity > 0 && (
        <>
         
        </>
      )} */
}
// const TtSummaryInfo: FC<SelectChildAgeProps> = (props) => {
//   const age2Str = useAge2Str();
//   const ls = props.value ?? [];
//   const childOnlyLs = ls.filter((x) => x.age !== "ADULT");
//   if (!childOnlyLs.length) return null;
//   return (
//     <p className="col-span-3 row-span-2 w-full font-normal text-sm leading-[1.25rem] flex items-center justify-end text-right text-[#000000]">
//       Children:{" "}
//       {childOnlyLs
//         .map((x) => {
//           if (!x.quantity) return null;
//           const { ageFrom: numAge } = ProdAttrUtils.CHILD.parseAge(x.age);
//           return `${x.quantity} (${age2Str(numAge)})`;
//         })
//         .filter((x) => x)
//         .join(", ")}
//     </p>
//   );
// };

const useAge2Str = () => {
  const { t } = useTranslation();
  const age2Str = (age: number) => {
    if (age === 0) {
      return t("Child <1 age");
    }
    return age > 1 ? t("years old", { age }) : t("year old", { age });
  };
  return age2Str;
};
const SelectChildAgeDropdownComponent: FC<
  SelectChildAgeProps & {
    onClose: () => void;
  }
> = ({ ages, onChange, ageQuantities, onClose, singleMode }) => {
  const { t } = useTranslation();
  ages = ages.sort((a, b) => a - b);
  const age2Str = useAge2Str();
  const [ls, setLs] = useState<AgeQuantityDTO[]>(ageQuantities ?? []);
  const adultOnlyLs = ls.filter((x) => x.age === "ADULT");
  const childOnlyLs = ls.filter((x) => x.age !== "ADULT");

  const ref = useRef<HTMLDivElement | null>(null);
  useClickOutside({
    ref,
    onOutsideClick: () => {
      onClose();
    },
  });

  return (
    <div
      ref={ref}
      className="absolute right-0 z-10 mt-[0.625rem] flex w-full flex-col rounded-[0.9375rem] border border-gray-300 bg-white p-3 shadow-md"
    >
      <div className="custom-scroll-bar max-h-[15rem] overflow-y-auto pr-2">
        {ages
          .filter((x) => (x ?? -1) >= 0)
          .map((age) => (
            <div
              key={age}
              className="flex h-12 w-full items-center justify-between"
            >
              <span
                className={clsx(
                  "text-sm font-normal leading-[19px] text-[#000000]",
                  {
                    "cursor-pointer hover:font-semibold": !!singleMode,
                    "!font-bold":
                      !!singleMode &&
                      childOnlyLs.some((x) => x.age === "CHILD_" + age),
                  },
                )}
                role={singleMode ? "button" : undefined}
                onClick={() => {
                  if (singleMode) {
                    const newLs = [
                      {
                        age: "CHILD_" + age,
                        quantity: childOnlyLs[0]?.quantity ?? 0,
                      },
                    ];
                    setLs(newLs);
                    onChange?.(newLs);
                    onClose();
                  }
                }}
              >
                {age2Str(age)}
              </span>
              {!singleMode && (
                <NumPicker
                  onChange={(newQty) => {
                    setLs((prev) => {
                      return ProdAttrUtils.CHILD.attachToAgeQuantityLs({
                        ls: prev,
                        age: "CHILD_" + age,
                        quantity: newQty,
                      });
                    });
                  }}
                  vari="SMALL"
                  qty={
                    childOnlyLs.find((x) => x.age === "CHILD_" + age)
                      ?.quantity ?? 0
                  }
                />
              )}
            </div>
          ))}
      </div>
      {!singleMode && (
        <div className="py-2.5">
          <button
            // disabled={
            //   maxPick !==
            //   childOnlyLs.map((x) => x.quantity).reduce((acc, x) => acc + x, 0)
            // }
            type="button"
            className="flex h-10 w-full items-center justify-center rounded-full bg-[#A8C6FF] text-center text-sm font-semibold leading-[1.1875rem] text-[#232323]"
            onClick={() => {
              onChange?.([...adultOnlyLs, ...childOnlyLs]);
              onClose();
            }}
          >
            {t("Apply")}
          </button>
        </div>
      )}
    </div>
  );
};
